import * as THREE from 'three';
import { jsondan } from '../FikoThreeType';
import { FikoGrup } from './FikoGrup';
import { FikoSahne } from './FikoSahne';

/**
 * 📝 FikoEtiketler - Annotation/Etiket yönetim sınıfı
 *
 * CATIA-style annotation sisteminin temel grubu
 * Ürün ağacında "Fiko-Etiketler" adıyla görünür
 *
 * Desteklenen etiket tipleri:
 * - Text/Note (Metin notu)
 * - Dimension (Boyut ölçüsü)
 * - Leader (Ok işaretçi)
 * - Flag (Bayrak işaretleyici)
 * - Geometric Tolerance (Geometrik tolerans)
 * - Datum Feature (Referans özellik)
 * - Surface Finish (Yüzey kalitesi)
 * - Weld Symbol (Kaynak sembolü)
 */
export class FikoEtiketler extends FikoGrup {
    override type: any = "FikoEtiketler";

    /** Etiket sayacı - Otomatik isimlendirme için */
    sayac = 1;

    /** Sağ araç çubuğu butonları - Annotation butonları floating toolbar'da olduğu için boş */
    sagButonlar = [];

    /** Liste - children'ı döndürür */
    public override get liste(): any[] {
        return this.children as any;
    }

    constructor(public override sahne?: FikoSahne, name?: string) {
        super(sahne);
        this.name = name || "Fiko-Etiketler";

        // Ürün ağacı görünüm ayarları
        this.userData['FikoArayuz'].agactaGorunur = true;
        this.userData['FikoArayuz'].listeGoster = false; // Alt elemanlar otomatik gösterilir
        this.userData['FikoArayuz'].simge = "./simgeler/etiketler-colorful.svg";
    }

    /**
     * 📝 Metin notu ekle
     */
    ekleMetinNotu(isim?: string): FikoGrup {
        const ekl = new FikoGrup(this.sahne, isim ? isim : `Metin Notu.${this.sayac}`);
        ekl.name = isim ? isim : `Metin Notu.${this.sayac}`;
        ekl.userData['FikoArayuz'].simge = "./simgeler/metin-notu.svg";
        ekl.userData['FikoEtiket'] = {
            tip: 'text',
            icerik: 'Yeni not...',
            renk: '#FFD700',
            boyut: 14
        };

        this.sayac += 1;
        return this.ekle(ekl) as any;
    }

    /**
     * 📏 Boyut ölçüsü ekle
     */
    ekleBoyutOlcusu(isim?: string): FikoGrup {
        const ekl = new FikoGrup(this.sahne, isim ? isim : `Boyut Ölçüsü.${this.sayac}`);
        ekl.name = isim ? isim : `Boyut Ölçüsü.${this.sayac}`;
        ekl.userData['FikoArayuz'].simge = "./simgeler/cetvel.webp";
        ekl.userData['FikoEtiket'] = {
            tip: 'dimension',
            baslangic: null,
            bitis: null,
            deger: 0,
            birim: 'mm'
        };

        this.sayac += 1;
        return this.ekle(ekl) as any;
    }

    /**
     * 🎯 Ok işaretçi ekle
     */
    ekleOkIsaretci(isim?: string): FikoGrup {
        const ekl = new FikoGrup(this.sahne, isim ? isim : `Ok İşaretçi.${this.sayac}`);
        ekl.name = isim ? isim : `Ok İşaretçi.${this.sayac}`;
        ekl.userData['FikoArayuz'].simge = "./simgeler/vektor-sec.webp";
        ekl.userData['FikoEtiket'] = {
            tip: 'leader',
            baslangic: null,
            bitis: null,
            metin: ''
        };

        this.sayac += 1;
        return this.ekle(ekl) as any;
    }

    /**
     * 🏷️ Bayrak işaretleyici ekle
     */
    ekleBayrak(isim?: string): FikoGrup {
        const ekl = new FikoGrup(this.sahne, isim ? isim : `Bayrak.${this.sayac}`);
        ekl.name = isim ? isim : `Bayrak.${this.sayac}`;
        ekl.userData['FikoArayuz'].simge = "./simgeler/bayrak.webp";
        ekl.userData['FikoEtiket'] = {
            tip: 'flag',
            pozisyon: null,
            metin: '',
            renk: '#FF0000'
        };

        this.sayac += 1;
        return this.ekle(ekl) as any;
    }

    /**
     * 📐 Geometrik tolerans ekle
     */
    ekleGeometrikTolerans(isim?: string): FikoGrup {
        const ekl = new FikoGrup(this.sahne, isim ? isim : `Geometrik Tolerans.${this.sayac}`);
        ekl.name = isim ? isim : `Geometrik Tolerans.${this.sayac}`;
        ekl.userData['FikoArayuz'].simge = "./simgeler/tolerans.svg";
        ekl.userData['FikoEtiket'] = {
            tip: 'tolerance',
            toleransTipi: 'flatness', // flatness, straightness, circularity, etc.
            deger: 0.01,
            datum: null,
            pozisyon: null
        };

        this.sayac += 1;
        return this.ekle(ekl) as any;
    }

    /**
     * 🔲 Datum özellik ekle
     */
    ekleDatumOzellik(isim?: string): FikoGrup {
        const ekl = new FikoGrup(this.sahne, isim ? isim : `Datum Özellik.${this.sayac}`);
        ekl.name = isim ? isim : `Datum Özellik.${this.sayac}`;
        ekl.userData['FikoArayuz'].simge = "./simgeler/datum.svg";
        ekl.userData['FikoEtiket'] = {
            tip: 'datum',
            datumHarfi: 'A', // A, B, C, etc.
            pozisyon: null,
            hedefYuzey: null
        };

        this.sayac += 1;
        return this.ekle(ekl) as any;
    }

    /**
     * ⚠️ Yüzey kalitesi ekle
     */
    ekleYuzeyKalite(isim?: string): FikoGrup {
        const ekl = new FikoGrup(this.sahne, isim ? isim : `Yüzey Kalitesi.${this.sayac}`);
        ekl.name = isim ? isim : `Yüzey Kalitesi.${this.sayac}`;
        ekl.userData['FikoArayuz'].simge = "./simgeler/yuzey-kalite.svg";
        ekl.userData['FikoEtiket'] = {
            tip: 'surface',
            raValue: 3.2, // Ra değeri (µm)
            islemTipi: 'machining', // machining, grinding, polishing, etc.
            pozisyon: null,
            hedefYuzey: null
        };

        this.sayac += 1;
        return this.ekle(ekl) as any;
    }

    /**
     * 🎨 Kaynak sembolü ekle
     */
    ekleKaynakSembol(isim?: string): FikoGrup {
        const ekl = new FikoGrup(this.sahne, isim ? isim : `Kaynak Sembolü.${this.sayac}`);
        ekl.name = isim ? isim : `Kaynak Sembolü.${this.sayac}`;
        ekl.userData['FikoArayuz'].simge = "./simgeler/kaynak.svg";
        ekl.userData['FikoEtiket'] = {
            tip: 'weld',
            kaynakTipi: 'fillet', // fillet, butt, groove, etc.
            boyut: 5, // mm
            uzunluk: null,
            pozisyon: null,
            hedefKenar: null
        };

        this.sayac += 1;
        return this.ekle(ekl) as any;
    }

    /**
     * 🏷️ CSS3D Annotation ekle - Profesyonel CAD-style HTML annotation
     */
    ekleCSS3DAnnotation(isim?: string): FikoGrup {
        const ekl = new FikoGrup(this.sahne, isim ? isim : `CSS3D Annotation.${this.sayac}`);
        ekl.name = isim ? isim : `CSS3D Annotation.${this.sayac}`;
        ekl.userData['FikoArayuz'].simge = "./simgeler/annotation.svg";
        ekl.userData['FikoEtiket'] = {
            tip: 'css3d-annotation',
            baslik: 'Annotation',
            aciklama: '',
            connectionPoints: []
        };

        this.sayac += 1;
        return this.ekle(ekl) as any;
    }

    /**
     * 🎯 Smart Point 3D ekle - CSS3D Renderer ile derinlik sıralamalı akıllı nokta
     */
    ekleSmartPoint3D(isim?: string, config?: {
        baslik?: string;
        aciklama?: string;
        kategori?: string;
        pozisyon?: { x: number; y: number; z: number };
        customIkon?: string;
        ozelRenk?: string;
        yaziRenk?: string;
        boyut?: number;
        pencereBoyut?: number;
    }): FikoGrup {
        const ekl = new FikoGrup(this.sahne, isim ? isim : `Smart Point.${this.sayac}`);
        ekl.name = isim ? isim : `Smart Point.${this.sayac}`;
        ekl.userData['FikoArayuz'].simge = "./simgeler/smart-point.svg";
        ekl.userData['FikoEtiket'] = {
            tip: 'smartpoint3d',
            baslik: config?.baslik || 'Nokta',
            aciklama: config?.aciklama || '',
            kategori: config?.kategori || 'info',
            pozisyon: config?.pozisyon || null,
            customIkon: config?.customIkon || '',
            ozelRenk: config?.ozelRenk || '',
            yaziRenk: config?.yaziRenk || '',
            boyut: config?.boyut || 48,
            pencereBoyut: config?.pencereBoyut || 100
        };

        this.sayac += 1;
        return this.ekle(ekl) as any;
    }

    /**
     * Tüm etiketleri temizle
     */
    tumunuTemizle(): void {
        while (this.children.length > 0) {
            const child = this.children[0];
            this.remove(child);
        }
        this.sayac = 1;

        // Sahneyi güncelle
        if (this.sahne?.anaGosterim) {
            this.sahne.anaGosterim.ciz();
        }
    }

    /**
     * Belirli tipteki etiketleri getir
     */
    getTipEtiketler(tip: 'text' | 'dimension' | 'leader' | 'flag' | 'tolerance' | 'datum' | 'surface' | 'weld' | 'css3d-annotation' | 'smartpoint3d'): FikoGrup[] {
        return this.children.filter((child: any) =>
            child.userData['FikoEtiket']?.tip === tip
        ) as FikoGrup[];
    }

    /**
     * JSON'dan yükle
     */
    fromJson(json?: any): void {
        if (!json || !json.children) return;

        for (let i = 0; i < json.children.length; i++) {
            const element = json.children[i];
            const child = jsondan(this.sahne, this, element);
            if (child) {
                this.ekle(child);
            }
        }
    }

    /**
     * JSON'a dönüştür
     */
    override toJSON(meta?: { geometries: any; materials: any; textures: any; images: any }): any {
        const _rslt: any = {
            type: this.type,
            isim: this.name,
            uuid: this.uuid
        };

        const childs: any[] = [];
        for (let index = 0; index < this.children.length; index++) {
            const element = this.children[index];
            if (typeof (element as any).toJSON === 'function') {
                childs.push((element as any).toJSON());
            }
        }
        _rslt.children = childs;

        return _rslt;
    }

    /**
     * JSON'dan FikoEtiketler oluştur
     */
    static fromJson(sahne?: FikoSahne, parent?: any, json?: any): FikoEtiketler {
        const _rslt = new FikoEtiketler(sahne, json.isim);
        _rslt.uuid = json.uuid;
        _rslt.parent = parent;

        if (json.children) {
            for (let i = 0; i < json.children.length; i++) {
                const element = json.children[i];
                const child = jsondan(sahne, _rslt, element);
                if (child) {
                    _rslt.ekle(child);
                }
            }
        }

        return _rslt;
    }

    /**
     * 📝 Dialog açma metotları - anaGosterim tarafından override edilecek
     */
    acDialogMetinNotu() {
    }

    acDialogBoyutOlcusu() {
    }

    acDialogOkIsaretci() {
    }

    acDialogBayrak() {
    }

    acDialogGeometrikTolerans() {
    }

    acDialogDatumOzellik() {
    }

    acDialogYuzeyKalite() {
    }

    acDialogKaynakSembol() {
    }
}
